import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {isNumber} from "lodash";
import {useAtom, useAtomValue} from "jotai";
import {Alert, Box, Button, Stack, Typography} from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import IconButton from "@mui/material/IconButton";
import {
    MRT_ColumnDef,
    MRT_ShowHideColumnsButton,
    MRT_TableInstance,
    MRT_ToggleFullScreenButton,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import {Parcel} from "../../types/domain/parcel";
import {activeFarmMaderaParcelsLoadable} from "../../store/activeFarmMaderaParcelsStore";
import {getCompanyMaderaReport} from "../../services/companyService";
import {GetMaderaReportOptions, MaderaReportLink, ReportType} from "../../types/domain/maderaReports";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {activeFarmLoadable} from "../../store/activeFarmStore.ts";
import {activeMergedFarmIds} from "../../store/activeMergedFarmIds.ts";
import {activeYearAtom} from "../../store/activeYearAtom.ts";
import {useParams} from "react-router-dom";

export const MaderaParcelUnitTable: React.FC = () => {
    const {t} = useTranslation();
    const parcels = useAtomValue(activeFarmMaderaParcelsLoadable);
    const {companyId} = useParams();
    const [enableToolbar, setEnableToolbar] = useState(false);

    const [error, setError] = useState<undefined | string>(undefined);
    const farmState = useAtomValue(activeFarmLoadable);
    const farm = farmState.state === "hasData" && farmState.data !== undefined ? farmState.data : undefined;
    const [mergedFarmIds] = useAtom(activeMergedFarmIds);
    const [activeYear] = useAtom(activeYearAtom);

    //We hide the download report button, for a merged farms(YEAR), because there are several farms.
    const isMergedFarmsGrouping = mergedFarmIds.length > 1;

    const handleException = (error: unknown) => {
        if (error instanceof Error) {
            setError(error.message);
        } else {
            setError("An unknown error occurred");
        }
    };

    const handleCloseAlert = () => {
        setError(undefined);
    };

    const handleDownload = async (params: GetMaderaReportOptions): Promise<void> => {
        if (companyId) {
            try {
                const data: MaderaReportLink = await getCompanyMaderaReport({
                    ...params,
                });
                window.open(data.url, "_blank");
            } catch (error) {
                handleException(error);
            }
        }
    };

    const handleDownloadAccountReport = () => {
        const companyId = farm ? farm.companyId : undefined;
        if (companyId !== undefined) {
            getCompanyMaderaReport({
                companyId: companyId,
                reportType: ReportType.ACCOUNT,
                year: activeYear,
                farmName: farm?.name,
            })
                .then((data) => {
                    window.open(data.url, "_blank");
                })
                .catch((error: unknown) => {
                    if (error instanceof Error) {
                        setError(`Error downloading report: ${error.message}`);
                    } else {
                        setError("An unknown error occurred");
                    }
                });
        }
    };

    const handleDownloadWrapper = (row: Parcel) => {
        if (companyId !== undefined) {
            handleDownload({
                parcel: row.name,
                year: row.year,
                reportType: ReportType.PARCEL,
                companyId,
            }).catch((error) => handleException(error));
        }
    };

    const columns: MRT_ColumnDef<Parcel>[] = [
        {
            id: "name",
            header: t("insights.madera.table.parcel"),
            accessorKey: "name",
            maxSize: 100,
        },
        {
            id: "assessedAcreage",
            header: t("insights.madera.table.assessed_acreage"),
            accessorKey: "assessedAcreage",
            maxSize: 100,
        },
        {
            id: "irrigationAcreage",
            header: t("insights.madera.table.irrigation_acreage"),
            accessorKey: "irrigationAcreage",
            maxSize: 100,
        },
        {
            id: "allocation",
            header: t("insights.madera.table.allocation"),
            accessorKey: "allocation",
            maxSize: 100,
        },
        {
            id: "carryover",
            header: t("insights.madera.table.carryover"),
            accessorKey: "carryover",
            maxSize: 100,
        },
        {
            id: "adjustment",
            header: t("insights.madera.table.adjustment"),
            accessorKey: "adjustment",
            maxSize: 100,
        },
        {
            id: "totalAllocation",
            header: t("insights.madera.table.total"),
            accessorKey: "totalAllocation",
            maxSize: 100,
        },
        {
            id: "etaw",
            header: t("insights.madera.table.etaw"),
            accessorFn: (row) => (isNumber(row.etaw) ? Math.round(row.etaw * 100) / 100 : undefined),
            maxSize: 100,
        },
        {
            id: "remaining",
            header: t("insights.madera.table.remaining"),
            accessorFn: (row) =>
                isNumber(row.etaw) ? Math.round((row.totalAllocation - (row.etaw ?? 0)) * 100) / 100 : undefined,
            maxSize: 100,
        },
        {
            id: "remainingpct",
            header: t("insights.madera.table.remaining_pct"),
            accessorFn: (row) =>
                isNumber(row.etaw)
                    ? Math.round(((row.totalAllocation - (row.etaw ?? 0)) / row.totalAllocation) * 10000) / 100 + "%"
                    : undefined,
            maxSize: 100,
        },
    ];

    const [locale] = useAtom(authenticatedUserTableLocaleAtom);
    const defaultTableOptions = getDefaultMRTOptions<Parcel>(locale);

    const table: MRT_TableInstance<Parcel> = useMaterialReactTable({
        ...defaultTableOptions, //spread your default options

        muiTableContainerProps: {
            sx: {
                maxHeight: "calc(100vh - 12rem)",
            },
        },
        initialState: {
            ...defaultTableOptions.initialState,
            columnPinning: {
                right: ["mrt-row-actions"],
            },
            pagination: {
                pageIndex: 0,
                pageSize: 20,
            },
        },
        columns,
        data: parcels.state === "hasData" ? parcels.data : [],
        muiTableBodyCellProps: {
            sx: {
                whiteSpace: "nowrap",
                paddingTop: "0.5rem",
                paddingBottom: "0.5rem",
                width: "1rem",
            },
        },
        enableColumnResizing: true,
        displayColumnDefOptions: {
            "mrt-row-actions": {
                enableResizing: false,
                size: 30,
                header: "",
            },
        },
        enableToolbarInternalActions: enableToolbar,
        enableTopToolbar: enableToolbar,
        muiTableHeadRowProps: () => ({sx: {backgroundColor: "#F3F5FA"}}),
        renderRowActions: ({row}) => (
            <Box sx={{display: "flex", gap: "1rem"}}>
                <IconButton color="primary" onClick={() => handleDownloadWrapper(row.original)}>
                    <DownloadIcon />
                </IconButton>
            </Box>
        ),
        state: {
            isLoading: parcels.state === "loading",
        },
    });

    const isFullScreen = table.getState().isFullScreen;
    useEffect(() => {
        setEnableToolbar(table.getState().isFullScreen);
    }, [table, isFullScreen]);

    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" height="4rem" alignItems="center">
                    <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                        Madera
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap="0.5rem">
                    {!isMergedFarmsGrouping && (
                        <Button
                            sx={{whiteSpace: "nowrap"}}
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadAccountReport()}
                        >
                            {t("insights.farm_insights_view.download_report")}
                        </Button>
                    )}
                    <MRT_ShowHideColumnsButton table={table} />
                    <MRT_ToggleFullScreenButton table={table} />
                </Stack>
            </Stack>
            {error && (
                <Alert severity="error" sx={{marginBottom: "1rem"}} onClose={handleCloseAlert}>
                    {error}
                </Alert>
            )}
            <MaterialReactTable table={table} />
        </>
    );
};
